/**
 * Дубли в StoreHouse — один документ заведён несколько раз (D89).
 *
 * «Удалил» прячет группу, пока чтение StoreHouse не подтвердит удаление;
 * «Разные» — документы на самом деле разные, больше не показывать.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@shell/api';
import { Empty } from '@shell/states';
import { portalApi } from '../../api';
import type { DuplicateGroup, Message } from '../../api';
import { Icon } from '../../icons';
import { Notice, isoDate } from '../../ui';

export function Duplicates({ groups, search = '' }: { groups: DuplicateGroup[]; search?: string }) {
  const shown = groups.filter(
    (group) =>
      !search ||
      `${group.supplier ?? ''} ${group.number}`.toLowerCase().includes(search),
  );
  if (shown.length === 0) {
    return <Empty title="Пусто" text={search ? 'Ничего не нашлось.' : 'Дублей в StoreHouse нет.'} />;
  }
  return (
    <ul className="dup-list">
      {shown.map((group) => (
        <DuplicateCard key={group.key} group={group} />
      ))}
    </ul>
  );
}

function DuplicateCard({ group }: { group: DuplicateGroup }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const mutation = useMutation<Message, ApiError, 'deleted' | 'different'>({
    mutationFn: (what) =>
      what === 'deleted'
        ? portalApi.duplicateDeleted(group.pair_id)
        : portalApi.duplicateDifferent(group.pair_id),
    onSuccess: (data) => {
      setResult({ ok: true, text: data.message });
      void queryClient.invalidateQueries({ queryKey: ['receipts'] });
      void queryClient.invalidateQueries({ queryKey: ['today'] });
    },
    onError: (error) =>
      setResult({
        ok: false,
        text: error.detail ?? 'Не получилось отметить. Попробуйте ещё раз.',
      }),
  });

  return (
    <li className="dup">
      <div className="dup__head">
        <span className="dup__supplier">{group.supplier ?? 'Поставщик не узнан'}</span>
        <span className="dup__count">×{group.count}</span>
      </div>
      <p className="dup__meta">
        № {group.number}
        {group.dates.length > 0 && ` · ${group.dates.map(isoDate).join(', ')}`}
      </p>
      <p className="dup__meta">
        {group.same_total ? 'суммы совпадают' : 'суммы разные — сверьте строки'}
        {group.posted > 0 && ` · проведено: ${group.posted}`}
      </p>
      {group.stale_mark && (
        <p className="dup__stale">Отмечали «Удалил», но в StoreHouse документ всё ещё есть.</p>
      )}
      {group.advice && (
        <p className="dup__advice">
          <Icon name="sparkle" size={14} />
          <span>{group.advice}</span>
        </p>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      <div className="dup__actions">
        <button
          type="button"
          className="button button--small button--primary"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate('deleted')}
        >
          Удалил
        </button>
        <button
          type="button"
          className="button button--small"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate('different')}
        >
          Разные
        </button>
      </div>
    </li>
  );
}
